import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import {
	Box,
	Button,
	TextField,
	InputAdornment,
	Divider
} from '@mui/material';
import {
	Search as SearchIcon,
	Refresh as RefreshIcon
} from '@mui/icons-material';
import { useErrorHandler } from '../../lib/errorHandler';

const TOOLBAR_GAP = 2;
const SEARCH_MIN_WIDTH = 220;
const ACTIONS_GAP = 1;

export interface BaseDataGridToolbarProps {
	searchValue?: string;
	onSearch?: (value: string) => void;
	onRefresh?: () => void;
	searchPlaceholder?: string;
	exportFunction?: (params: URLSearchParams) => Promise<void>;
	/** Params của chính truy vấn đang hiển thị trên lưới (không page/limit). */
	exportParams?: URLSearchParams;
	showExport?: boolean;
	isMobile?: boolean;
}

export const BaseDataGridToolbar: React.FC<BaseDataGridToolbarProps> = ({
	searchValue = '',
	onSearch,
	onRefresh,
	searchPlaceholder = 'Tìm kiếm...',
	exportFunction,
	showExport = true,
	exportParams,
	isMobile = false,
}) => {
	const { showError } = useErrorHandler();
	const [localSearchValue, setLocalSearchValue] = useState(searchValue);
	const searchTimeoutRef = useRef<number>();

	useEffect(() => {
		setLocalSearchValue(searchValue);
	}, [searchValue]);

	useEffect(() => {
		const currentTimeout = searchTimeoutRef.current;
		return () => {
			if (currentTimeout) {
				clearTimeout(currentTimeout);
			}
		};
	}, []);

	const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setLocalSearchValue(event.target.value);
	};

	const handleSearchSubmit = useCallback(() => {
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}
		onSearch?.(localSearchValue);
	}, [localSearchValue, onSearch]);

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleSearchSubmit();
		}
	};

	const handleExport = async () => {
		if (!exportFunction) {
			showError('Chức năng xuất Excel không khả dụng');
			return;
		}

		try {
			await exportFunction(new URLSearchParams(exportParams));
		} catch (error) {
			console.error('[ERROR] Export Excel error:', error);
			showError('Có lỗi xảy ra khi tải xuống file Excel. Vui lòng thử lại.');
		}
	};

	return (
		<GridToolbarContainer sx={{ p: 2, gap: TOOLBAR_GAP, flexWrap: 'wrap' }}>
			<Box sx={{ display: 'flex', gap: ACTIONS_GAP, alignItems: 'center', flexWrap: 'wrap' }}>
				{!isMobile && (
					<>
						<GridToolbarColumnsButton />
						<GridToolbarDensitySelector />
						<Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
					</>
				)}

				<Button
					size="small"
					startIcon={<RefreshIcon />}
					onClick={onRefresh}
					variant="outlined"
				>
					Làm mới
				</Button>

				{showExport && exportFunction && (
					<Button
						size="small"
						onClick={handleExport}
						variant="outlined"
						color="success"
					>
						Xuất Excel
					</Button>
				)}
			</Box>

			{/* Right side - Search */}
			<Box sx={{
				marginLeft: 'auto',
				display: 'flex',
				gap: ACTIONS_GAP,
				alignItems: 'center',
				flexWrap: { xs: 'nowrap', sm: 'wrap' },
				width: { xs: '100%', sm: 'auto' }
			}}>
				<TextField
					size="small"
					placeholder={searchPlaceholder}
					value={localSearchValue}
					onChange={handleSearchInputChange}
					onKeyDown={handleKeyPress}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}
					}}
					sx={{
						flexGrow: { xs: 1, sm: 0 },
						minWidth: { xs: 0, sm: SEARCH_MIN_WIDTH }
					}}
				/>
				<Button
					size="small"
					variant="contained"
					onClick={handleSearchSubmit}
					startIcon={<SearchIcon />}
					sx={{ flexShrink: 0 }}
				>
					Tìm
				</Button>
			</Box>
		</GridToolbarContainer>
	);
};